import Api from '@/modules/core/api/api.ts';
import { RouteEndPoint } from '@/modules/common/enums/end-point.ts';
import TemplateFile from '@/modules/document/models/file/template-file.ts';
import type DocumentFile from '@/modules/document/models/file/document-file.ts';
import DocumentApi from '@/modules/document/api/document-api.ts';
import CacheGroup from '@/modules/common/enums/cache-group.ts';
import { ContractorOption } from '@/modules/common/models/option/contractor-option.ts';
import { RequestUtil } from '@/modules/core/utils/request.util.ts';
import { Direction } from '@/modules/common/enums/direction.enum.ts';
import type { ContractorResponse, FileTemplateResponse } from '@/modules/core/api/route-api.type.ts';
import { EDITABLE_FILE_TYPES } from '@/modules/common/consts/file.ts';
import type { ItemsDto } from '@/modules/common/dto/items.dto.ts';
import GatewayApi from '@/modules/core/api/gateway.api.ts';
import { GatewayFilter } from '@/modules/core/utils/gateway-filter.ts';
import { Operator } from '@/modules/viewer/enums/operator.enum.ts';

export default class RouteApi {
  private static readonly EDITABLE_TEMPLATES_FILTER = GatewayFilter.or(
    ...EDITABLE_FILE_TYPES.map((type) => GatewayFilter.condition('Название шаблона', Operator.EndsWith, `.${type}`))
  );

  static async getFileTemplates(ids: number[], signal?: AbortSignal, editableOnly = false) {
    const response = await GatewayApi.execute<ItemsDto<FileTemplateResponse>>(RouteEndPoint.GetFileTemplates, {
      params: { doc_ids: ids },
      filter: editableOnly ? RouteApi.EDITABLE_TEMPLATES_FILTER : undefined,
      signal,
      cacheConfig: {
        group: CacheGroup.DocumentMaster,
        key: `FILE_TEMPLATES_${editableOnly ? 'EDITABLE_' : ''}${ids.join('_')}`,
      },
    });

    const filesByDocId = new Map<number, Promise<DocumentFile[]>>();

    for (const { DOC_ID } of response.items) {
      if (!filesByDocId.has(DOC_ID)) {
        filesByDocId.set(
          DOC_ID,
          DocumentApi.getFiles(DOC_ID, signal, {
            group: CacheGroup.DocumentMaster,
            key: `DOCUMENT_${DOC_ID}`,
          })
        );
      }
    }

    return await Promise.all(
      response.items.map(async (template) => {
        const files = await filesByDocId.get(template.DOC_ID)!;
        const file = files.find((file) => file.fileId === template.F_ID);

        if (!file) {
          throw new Error(`Файл F_ID=${template.F_ID} не найден в документе DOC_ID=${template.DOC_ID}.`);
        }

        return new TemplateFile(template, file);
      })
    );
  }

  static async getContractors(signal?: AbortSignal): Promise<ContractorOption[]> {
    const response: { items: ContractorResponse[] } = await Api.post(
      RouteEndPoint.GetContractors,
      RequestUtil.orderBy('Исполнитель', Direction.Asc),
      signal,
      true,
      { group: CacheGroup.DocumentMaster, key: 'CONTRACTORS' }
    );

    return response.items.map((item) => new ContractorOption(item));
  }
}
